// The client-side in-memory MODEL: one columnar store per data source, shared
// by every visual on the dashboard that draws from it.
//
// The server ships one query per source at a grain covering all its visuals
// (backend/app/services/visual_model_service.py). This holds those rows in a
// ProPivot columnar store (typed arrays + dictionary encoding) and folds each
// visual out of it on demand — so N tile queries became one, and every later
// interaction (quick filter, the ⋮ Filter dialog) is a local recompute instead
// of a round trip.
//
// Filtering goes through the SAME evaluator the non-model path uses
// (visualFilter.ts): the expression is applied to the model's rows, and the
// surviving rows are re-ingested before the fold. One filter semantics for
// every surface, model-backed or not.

import { useEffect, useMemo, useState } from 'react'
import { ensureProPivotEngine } from './loader'
import { planToReport, matrixToJsonData } from './planToReport'
import type { ProPivotMatrix, VisualPlan } from './planToReport'
import { combineFilters, evaluateFilterExpression, filterKey, hasFilterRules } from './visualFilter'
import type { FilterExpression } from './visualFilter'
import type { VisualJsonData } from './types'

/** One source's dataset as returned by the dashboard endpoint. */
export interface VisualModelPayload {
  key: string
  jsonData?: VisualJsonData
  rowCount?: number
  truncated?: boolean
  sourceSql?: string
}

// A ProPivot LocalEngine, minimally typed (the library is aliased per consumer).
interface LocalEngineLike {
  setData(data: unknown, mapping?: unknown): void
  computeSync(report: unknown): ProPivotMatrix
  dispose(): void
}
type LocalEngineCtor = new () => LocalEngineLike

// Distinct filtered row sets kept ingested at once. A dashboard realistically
// holds the unfiltered set plus the current filter; the cap stops a long
// filtering session from pinning every intermediate store in memory.
const MAX_STORES = 3

/**
 * One source's rows, plus the engine that folds them.
 *
 * `compute()` is synchronous: models are capped at the server's visual row
 * ceiling, so the fold is a tight in-memory pass and React can render the result
 * in the same tick. (A detail-grain model would want ProPivot's WorkerEngine —
 * the seam is `#engineFor`.)
 */
export class VisualModel {
  readonly key: string
  readonly rows: Array<Record<string, unknown>>
  readonly truncated: boolean
  private readonly Engine: LocalEngineCtor
  private readonly stores = new Map<string, { engine: LocalEngineLike; rows: Array<Record<string, unknown>> }>()
  private readonly results = new Map<string, VisualJsonData>()

  constructor(payload: VisualModelPayload, Engine: LocalEngineCtor) {
    this.key = payload.key
    this.Engine = Engine
    this.truncated = Boolean(payload.truncated)
    const json = payload.jsonData || []
    this.rows = (json.slice(1) as Array<Record<string, unknown>>) || []
  }

  /** Column names the model actually carries (from its first row / schema). */
  get columns(): string[] {
    const first = this.rows[0]
    return first ? Object.keys(first).filter((k) => k !== 'id') : []
  }

  /**
   * Fold this model into one visual's dataset.
   *
   * `plan.filter` carries the visual's OWN criteria — the rules the server could
   * not push into the shared query, because the model holds the rows every
   * visual on this source needs. They must be applied here or the visual folds
   * the whole source and reports someone else's population as its own. They AND
   * with the interactive filter (dashboard quick filter + the ⋮ dialog), and the
   * fold runs over the survivors, so an aggregate is recomputed over exactly the
   * rows this visual's own query would have returned.
   */
  compute(plan: VisualPlan, filter?: FilterExpression | null): VisualJsonData {
    const effective = combineFilters(plan.filter as FilterExpression | null, filter)
    const cacheKey = `${planKey(plan)}|${filterKey(effective)}`
    const cached = this.results.get(cacheKey)
    if (cached) return cached
    const { engine, rows } = this.engineFor(effective)
    const matrix = engine.computeSync(planToReport(plan, rows))
    const json = matrixToJsonData(matrix, plan)
    this.results.set(cacheKey, json)
    return json
  }

  /** Rows surviving the expression — the shared evaluator, over model columns. */
  private filteredRows(filter?: FilterExpression | null): Array<Record<string, unknown>> {
    if (!hasFilterRules(filter)) return this.rows
    const byLower = new Map<string, string>()
    for (const column of this.columns) byLower.set(column.toLowerCase(), column)
    const resolve = (column: string) => byLower.get(String(column).toLowerCase())
    return this.rows.filter((row) => evaluateFilterExpression(row, filter!, resolve))
  }

  private engineFor(filter?: FilterExpression | null) {
    const key = filterKey(filter)
    const hit = this.stores.get(key)
    if (hit) return hit
    const rows = this.filteredRows(filter)
    const engine = new this.Engine()
    engine.setData(rows)
    const entry = { engine, rows }
    this.stores.set(key, entry)
    // Evict the oldest ingested store (insertion-ordered Map) past the cap.
    if (this.stores.size > MAX_STORES) {
      const oldest = this.stores.keys().next().value as string | undefined
      if (oldest !== undefined && oldest !== key) {
        try { this.stores.get(oldest)?.engine.dispose() } catch { /* best effort */ }
        this.stores.delete(oldest)
      }
    }
    return entry
  }

  dispose(): void {
    for (const { engine } of this.stores.values()) {
      try { engine.dispose() } catch { /* best effort */ }
    }
    this.stores.clear()
    this.results.clear()
  }
}

/** Identity of a plan for caching — its shape, not its object reference. */
export function planKey(plan: VisualPlan): string {
  return JSON.stringify({
    d: (plan.dims || []).map((x) => x.modelColumn),
    s: (plan.series || []).map((x) => x.modelColumn),
    m: (plan.measures || []).map((x) => [x.column, x.agg, x.modelColumn, x.derive]),
    t: plan.topN,
    o: plan.sort,
  })
}

export interface VisualModelSet {
  /** Ready models by source key (empty until the engine has loaded). */
  models: Map<string, VisualModel>
  /** True while the engine module is loading — tiles should hold their spinner. */
  loading: boolean
  error: string | null
}

/**
 * Build the models for a dashboard payload and keep them until it changes.
 *
 * The engine is lazily imported (it is not in the main bundle), so this reports
 * `loading` for the first tick — a model-backed tile has no server dataset of
 * its own to show in the meantime, by design: that query was never run.
 */
export function useVisualModels(payloads: VisualModelPayload[] | undefined): VisualModelSet {
  const [Engine, setEngine] = useState<LocalEngineCtor | null>(null)
  const [error, setError] = useState<string | null>(null)
  const wanted = (payloads || []).filter((p) => p && p.key && !p.truncated)

  useEffect(() => {
    if (Engine || !wanted.length) return
    let cancelled = false
    ensureProPivotEngine()
      .then((mod) => {
        if (cancelled) return
        const ctor = (mod as { LocalEngine?: LocalEngineCtor })?.LocalEngine
        if (!ctor) throw new Error('ProPivot LocalEngine unavailable')
        setEngine(() => ctor)
      })
      .catch((e) => {
        if (cancelled) return
        console.error('[visual-model] engine load failed', e)
        setError(e instanceof Error ? e.message : String(e))
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Engine, wanted.length])

  // Rebuild whenever the host hands us a DIFFERENT payload array — which is
  // exactly once per fetch, since every host holds the response in state
  // (`useVisualModels(data?.models)`), so a re-render with unchanged data keeps
  // the ingested stores and a re-run replaces them.
  //
  // It must not be a content summary. Keying on `key + rowCount` looks
  // equivalent and is not: changing a criterion that is PUSHED INTO the model
  // query gives the same source key and very often the same row count (one row
  // per head per date, whichever branch you pick) with entirely different
  // numbers — and the stale store would be reused, so every folded tile keeps
  // reporting the PREVIOUS parameter's data. Auto-refresh has the same shape.
  // Identity is both cheaper and strictly correct here.
  const models = useMemo(() => {
    const map = new Map<string, VisualModel>()
    if (!Engine) return map
    for (const payload of wanted) map.set(payload.key, new VisualModel(payload, Engine))
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Engine, payloads])

  // Release the previous generation's stores when the payload set changes.
  useEffect(() => () => { for (const model of models.values()) model.dispose() }, [models])

  return {
    models,
    loading: Boolean(wanted.length) && !Engine && !error,
    error,
  }
}

/** Machine reason → why this visual is queried live instead of folded. */
export const MODEL_FALLBACK_REASON_TEXT: Record<string, string> = {
  sql_mode: 'it is a raw-SQL visual, so its query is opaque to the shared model',
  report_tile: 'report tiles run their own report query',
  no_aggregate: 'it lists detail rows, which a grouped model would collapse',
  holistic_agg: 'it uses an aggregation (distinct count / median / deviation) that cannot be folded from a grouped model',
  masked_measure: 'one of its measures is masked for your role',
  expression_measure: 'one of its measures is a calculated column that aggregates in SQL (a ratio or conditional total), which cannot be recomputed from a grouped model',
  detail_grain: 'it is grouped at the detail grain, so sharing a dataset with the other tiles would exceed the row limit — it is queried on its own instead',
  untranslatable_criteria: 'one of its filters cannot be applied in the browser',
  having_condition: 'it filters on an aggregate (HAVING), which the model cannot carry',
  needs_input: 'it is waiting for a criteria value',
  criteria_checks: 'its criteria have checks that run with its own query',
  events: 'it shows events on its timeline, which come with its own query',
  model_too_large: 'the shared dataset exceeded the row limit',
  model_failed: 'the shared dataset could not be built',
  no_source: 'it has no visual assigned',
}
